import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import ApiPaginateSearch from "../../component/ApiPaginateSearch";
import ApiEndpoints from "../../network/ApiEndPoints";
import { BASE_URL } from "../../network/ApiEndPoints";
import AuthContext from "../../store/AuthContext";

const Performer = () => {
  const [apiData, setApiData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const authCtx = useContext(AuthContext);
  const token = authCtx.token;
  const apiEnd = ApiEndpoints.GET_USERS_BIGG;
  const queryParam = "role=Developer";

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/${apiEnd}?${queryParam}&export=1`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        console.log("API Response:", response.data);

        // Check the structure of your API response
        const responseData = response.data;
        
        // Extract data based on common API response structures
        let dataArray = [];
        let total = 0;
        
        if (Array.isArray(responseData.data)) {
          dataArray = responseData.data;
          total = responseData.total || responseData.count || dataArray.length;
        } else if (responseData.data && Array.isArray(responseData.data.data)) {
          dataArray = responseData.data.data;
          total = responseData.data.total || responseData.data.count || dataArray.length;
        } else if (Array.isArray(responseData)) {
          dataArray = responseData;
          total = responseData.length;
        }

        const dataWithMetrics = dataArray.map((developer) => {
          const totalWeightage =
            developer.tasks?.reduce((sum, task) => {
              return sum + (task.weightage || 0);
            }, 0) || 0;

          const completedWeightage =
            developer.tasks?.reduce((sum, task) => {
              return task.status === "COMPLETED"
                ? sum + (task.weightage || 0)
                : sum;
            }, 0) || 0;

          return {
            ...developer,
            totalWeightage,
            completedWeightage,
            weightagePercentage:
              totalWeightage > 0
                ? (completedWeightage / totalWeightage) * 100
                : 0,
            // Add these if they don't exist in your API response
            tasksCount: developer.tasksCount || developer.tasks?.length || 0,
            completeCount: developer.completeCount || developer.tasks?.filter(t => t.status === "COMPLETED").length || 0,
          };
        });

        // Sort locally if needed (or let backend handle sorting)
        const sorted = [...dataWithMetrics].sort((a, b) => {
          if (b.completedWeightage !== a.completedWeightage) {
            return b.completedWeightage - a.completedWeightage;
          }
          return b.weightagePercentage - a.weightagePercentage;
        });

        setApiData(sorted);
        setSortedData(sorted);
        setTotalCount(total);
      } catch (error) {
        console.error("Error fetching performer data:", error);
        setApiData([]);
        setSortedData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiEnd, queryParam, token, page, rowsPerPage]); // Add page and rowsPerPage as dependencies

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  const renderProgressBar = (tasksCount, completeCount) => {
    const percentage =
      tasksCount > 0 ? ((completeCount * 100) / tasksCount).toFixed(1) : 0;
    return (
      <div style={{ width: "100%" }}>
        <div
          style={{
            height: "22px",
            backgroundColor: "#e5e7eb",
            borderRadius: "10px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${percentage}%`,
              backgroundColor: "rgb(227, 210, 246)",
              height: "100%",
              transition: "width 0.3s ease-in-out",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
              fontSize: "14px",
              lineHeight: "22px",
              color: "rgb(152, 9, 224)",
            }}
          >
            {percentage}%
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
        Top Performers (Weightage Based)
      </Typography>

      <Paper sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Rank</strong></TableCell>
              <TableCell><strong>Developer Name</strong></TableCell>
              <TableCell><strong>Assigned Task</strong></TableCell>
              <TableCell><strong>Completed Task</strong></TableCell>
              <TableCell><strong>Pending Task</strong></TableCell>
              <TableCell><strong>Task Weightage</strong></TableCell>
              <TableCell><strong>Completed Weightage</strong></TableCell>
              <TableCell><strong>Completed Weightage %</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((row, index) => (
                <TableRow key={row.id || index}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600 }}>
                      {page * rowsPerPage + index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar
                        alt={row.name}
                        src={row?.profile_image || ""}
                        sx={{ width: 40, height: 40, mr: 2 }}
                      >
                        {row.name ? row.name.charAt(0).toUpperCase() : "?"}
                      </Avatar>
                      <Typography sx={{ textTransform: "capitalize", fontWeight: 600 }}>
                        {row.name || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      color: "#3b82f6",
                      backgroundColor: "#e0f2fe",
                      fontWeight: "bold",
                      px: 2,
                      py: 0.5,
                      borderRadius: "8px",
                      textAlign: "center",
                    }}>
                      {row.tasksCount || 0}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      color: "#10b981",
                      backgroundColor: "#ecfdf5",
                      fontWeight: "bold",
                      px: 2,
                      py: 0.5,
                      borderRadius: "8px",
                      textAlign: "center",
                    }}>
                      {row.completeCount || 0}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      color: "#f97316",
                      backgroundColor: "#fff7ed",
                      fontWeight: "bold",
                      px: 2,
                      py: 0.5,
                      borderRadius: "8px",
                      textAlign: "center",
                    }}>
                      {(row.tasksCount || 0) - (row.completeCount || 0)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      color: "#8b5cf6",
                      backgroundColor: "#f3e8ff",
                      fontWeight: "bold",
                      px: 2,
                      py: 0.5,
                      borderRadius: "8px",
                      textAlign: "center",
                    }}>
                      {row.totalWeightage || 0}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{
                      color: "#047857",
                      backgroundColor: "#d1fae5",
                      fontWeight: "bold",
                      px: 2,
                      py: 0.5,
                      borderRadius: "8px",
                      textAlign: "center",
                    }}>
                      {row.completedWeightage || 0}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {renderProgressBar(row.tasksCount || 0, row.completeCount || 0)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography>No data available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount} // Use total count from API
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </Paper>
    </Box>
  );
};

export default Performer;